/*
 * The selling price of a calculation row for a contribution margin typed by the user.
 *
 * The row's selling price in the calculation is
 *   sellingPrice × quantity   when the selling price is given per unit (e.g. €/m²)
 *   sellingPrice              when it is given as a sum (€)
 * and the margin is
 *   margin €  = row selling price − row expense
 *   margin %  = margin € / row selling price × 100
 * so for a typed margin
 *   row selling price = expense + margin €
 *   row selling price = expense / (1 − margin % / 100)
 * and the selling price is the row selling price divided by the quantity factor. The
 * selling price is rounded up to the next cent, so the margin shown afterwards is at least
 * the typed one (e.g. 25 % of 10,00 € gives 13,34 € and 25,04 %).
 */

// A floating point error (e.g. 200.00000000000003) is not rounded up to the next cent
const CENT_TOLERANCE = 1e-6;

// + 0 turns -0 (a price of exactly 0) into 0
const roundUpToCents = (value: number): number =>
  Math.ceil(value * 100 - CENT_TOLERANCE) / 100 + 0;

/** 1 when the selling price is a sum (€), otherwise the quantity of the row */
export const getSellingPriceQuantityFactor = (
  sellingPriceUnit: string | null,
  quantity: number
): number => ([null, ''].includes(sellingPriceUnit) ? 1 : quantity);

/**
 * The selling price giving the margin in euros, or null when it cannot be calculated
 * (no quantity, or a selling price below zero).
 */
export const getSellingPriceForMarginEur = (
  expense: number,
  marginEur: number,
  quantityFactor: number
): number | null => {
  if (!(quantityFactor > 0) || !Number.isFinite(marginEur)) {
    return null;
  }

  const rowSellingPrice = expense + marginEur;
  if (rowSellingPrice < 0) {
    return null;
  }

  return roundUpToCents(rowSellingPrice / quantityFactor);
};

/**
 * The selling price giving the margin percentage, or null when it cannot be calculated
 * (no quantity or expense, or a margin of 100 % or more).
 */
export const getSellingPriceForMarginPercent = (
  expense: number,
  marginPercent: number,
  quantityFactor: number
): number | null => {
  if (
    !(quantityFactor > 0) ||
    !(expense > 0) ||
    !Number.isFinite(marginPercent) ||
    marginPercent >= 100
  ) {
    return null;
  }

  const rowSellingPrice = expense / (1 - marginPercent / 100);
  return roundUpToCents(rowSellingPrice / quantityFactor);
};
